import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Spa as SpaIcon,
  Block as BlockIcon,
  CalendarViewWeek as CalendarViewIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Schedule as ScheduleIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  EventNote as EventNoteIcon,
  ContentCut as ContentCutIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
  AutoAwesome as WandIcon,
} from '@mui/icons-material';

import AppointmentList from './AppointmentList';
import ServiceManagement from './ServiceManagement';
import BlockedTimeManagement from './BlockedTimeManagement';
import CalendarView from './CalendarView';
import TeamManagement from './TeamManagement';
import CategoryManagement from './CategoryManagement';
import SalonScheduleManagement from './SalonScheduleManagement';
import SpecialRequestManagement from './SpecialRequestManagement';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { teamApi } from '../../firebase/services';
import { TeamMember } from '../../types';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED = 72;

type NavItem = {
  id: number;
  label: string;
  icon: React.ReactNode;
  group?: string;
};

const NAV_ITEMS: NavItem[] = [
  { id: 0, label: 'Rendez-vous',        icon: <CalendarIcon />,     group: 'Gestion' },
  { id: 7, label: 'Calendrier',         icon: <CalendarViewIcon />, group: 'Gestion' },
  { id: 8, label: 'Dem. spécialisées',  icon: <WandIcon />,         group: 'Gestion' },
  { id: 1, label: 'Services',           icon: <SpaIcon />,          group: 'Catalogue' },
  { id: 5, label: 'Catégories',         icon: <CategoryIcon />,     group: 'Catalogue' },
  { id: 3, label: 'Équipe',             icon: <PeopleIcon />,       group: 'Catalogue' },
  { id: 2, label: 'Horaires bloqués',   icon: <BlockIcon />,        group: 'Paramètres' },
  { id: 6, label: 'Horaires salon',     icon: <ScheduleIcon />,     group: 'Paramètres' },
];

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  loading?: boolean;
}> = ({ label, value, icon, color, bg, loading }) => (
  <Paper
    elevation={0}
    sx={{
      flex: '1 1 160px',
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      background: '#fff',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2.5,
        bgcolor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        flexShrink: 0,
        '& svg': { fontSize: 24 },
      }}
    >
      {icon}
    </Box>
    <Box>
      {loading ? (
        <CircularProgress size={20} sx={{ color }} />
      ) : (
        <Typography variant="h5" fontWeight={700} lineHeight={1} color="text.primary">
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeNav, setActiveNav] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const {
    services, appointments, blockedTimes, categories, salonSettings,
    loading, refreshServices, refreshAppointments, refreshBlockedTimes,
    refreshCategories, refreshSalonSettings,
  } = useApp();
  const { userData, logout } = useAuth();

  const refreshTeamMembers = async () => {
    setTeamLoading(true);
    try { setTeamMembers(await teamApi.getAll()); }
    catch (e) { console.error(e); }
    finally { setTeamLoading(false); }
  };

  React.useEffect(() => {
    refreshTeamMembers();
  }, []);

  if (!userData || userData.role !== 'admin') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Accès refusé. Contactez l'administrateur.
        </Alert>
      </Box>
    );
  }

  const upcomingCount = appointments.filter(a => {
    const dt = new Date(`${a.date}T${a.startTime}`);
    return dt >= new Date() && (a as any).status !== 'cancelled';
  }).length;

  const activeNav_label = NAV_ITEMS.find(n => n.id === activeNav)?.label ?? '';
  const sidebarWidth = collapsed && !isMobile ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  const handleNav = (id: number) => {
    setActiveNav(id);
    if (isMobile) setMobileOpen(false);
  };

  // Group nav items
  const groups = ['Gestion', 'Catalogue', 'Paramètres'];

  const SidebarContent = () => (
    <Box
      sx={{
        width: sidebarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0F172A',
        transition: 'width 0.25s ease',
        overflow: 'hidden',
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          px: collapsed && !isMobile ? 1.5 : 2.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          minHeight: 72,
        }}
      >
        {(!collapsed || isMobile) && (
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: '#fff', lineHeight: 1.2, fontSize: '0.9rem' }}
            >
              Desiree African
            </Typography>
            <Typography variant="caption" sx={{ color: '#B8A04A', fontWeight: 600 }}>
              Hair Braiding
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={() => setCollapsed(p => !p)}
            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
          >
            {collapsed ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2 }}>
        {groups.map((group) => {
          const items = NAV_ITEMS.filter(n => n.group === group);
          return (
            <Box key={group} sx={{ mb: 1 }}>
              {(!collapsed || isMobile) && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 2.5,
                    py: 0.5,
                    display: 'block',
                    color: 'rgba(255,255,255,0.3)',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  {group}
                </Typography>
              )}
              {items.map((item) => {
                const isActive = activeNav === item.id;
                return (
                  <Tooltip
                    key={item.id}
                    title={collapsed && !isMobile ? item.label : ''}
                    placement="right"
                  >
                    <Box
                      onClick={() => handleNav(item.id)}
                      sx={{
                        mx: 1.5,
                        mb: 0.5,
                        px: collapsed && !isMobile ? 1.5 : 2,
                        py: 1.1,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                        bgcolor: isActive ? 'rgba(196,30,58,0.15)' : 'transparent',
                        color: isActive ? '#C41E3A' : 'rgba(255,255,255,0.6)',
                        borderLeft: isActive ? '3px solid #C41E3A' : '3px solid transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: isActive ? 'rgba(196,30,58,0.2)' : 'rgba(255,255,255,0.07)',
                          color: isActive ? '#C41E3A' : '#fff',
                        },
                        '& svg': { fontSize: 20, flexShrink: 0 },
                      }}
                    >
                      {item.icon}
                      {(!collapsed || isMobile) && (
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 600 : 400}
                          sx={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                        >
                          {item.label}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* User area */}
      <Box
        sx={{
          px: collapsed && !isMobile ? 1 : 2,
          py: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: '#C41E3A',
            fontSize: '0.85rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {userData.firstName?.charAt(0).toUpperCase()}
        </Avatar>
        {(!collapsed || isMobile) && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {userData.firstName} {userData.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                Admin
              </Typography>
            </Box>
            <Tooltip title="Déconnexion">
              <IconButton
                size="small"
                onClick={logout}
                sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );

  const renderContent = () => {
    const loadingSpinner = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#C41E3A' }} />
      </Box>
    );

    switch (activeNav) {
      case 0:
        return loading.appointments ? loadingSpinner : (
          <AppointmentList appointments={appointments} services={services} teamMembers={teamMembers} salonSettings={salonSettings} onRefresh={refreshAppointments} />
        );
      case 1:
        return loading.services ? loadingSpinner : (
          <ServiceManagement services={services} categories={categories} onRefresh={refreshServices} />
        );
      case 2:
        return loading.blockedTimes ? loadingSpinner : (
          <BlockedTimeManagement blockedTimes={blockedTimes} teamMembers={teamMembers} onRefresh={refreshBlockedTimes} />
        );
      case 3:
        return teamLoading ? loadingSpinner : <TeamManagement />;
      case 5:
        return <CategoryManagement categories={categories} onRefresh={refreshCategories} />;
      case 6:
        return <SalonScheduleManagement settings={salonSettings} onRefresh={refreshSalonSettings} />;
      case 7:
        return <CalendarView appointments={appointments} services={services} blockedTimes={blockedTimes} teamMembers={teamMembers} />;
      case 8:
        return <SpecialRequestManagement />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8F9FB' }}>

      {/* Sidebar — desktop */}
      {!isMobile && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 1200,
            transition: 'width 0.25s ease',
          }}
        >
          <SidebarContent />
        </Box>
      )}

      {/* Sidebar — mobile drawer */}
      {isMobile && (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH, bgcolor: 'transparent', boxShadow: 'none' } }}
        >
          <SidebarContent />
        </Drawer>
      )}

      {/* Main area */}
      <Box
        sx={{
          flex: 1,
          ml: isMobile ? 0 : `${sidebarWidth}px`,
          transition: 'margin-left 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Top header */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: 2,
            bgcolor: '#fff',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} size="small">
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Tableau de bord
              </Typography>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2} color="text.primary">
                {activeNav_label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600}>
                {userData.firstName} {userData.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Administrateur
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: '#C41E3A',
                fontSize: '0.9rem',
                fontWeight: 700,
              }}
            >
              {userData.firstName?.charAt(0).toUpperCase()}
            </Avatar>
          </Box>
        </Box>

        {/* Stats row */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 3,
            pb: 1,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <StatCard
            label="Rendez-vous à venir"
            value={upcomingCount}
            icon={<EventNoteIcon />}
            color="#C41E3A"
            bg="rgba(196,30,58,0.1)"
            loading={loading.appointments}
          />
          <StatCard
            label="Services actifs"
            value={services.length}
            icon={<ContentCutIcon />}
            color="#B8A04A"
            bg="rgba(184,160,74,0.12)"
            loading={loading.services}
          />
          <StatCard
            label="Membres équipe"
            value={teamMembers.length}
            icon={<PeopleIcon />}
            color="#1976D2"
            bg="rgba(25,118,210,0.1)"
            loading={teamLoading}
          />
          <StatCard
            label="Plages bloquées"
            value={blockedTimes.length}
            icon={<AccessTimeIcon />}
            color="#7B1FA2"
            bg="rgba(123,31,162,0.1)"
            loading={loading.blockedTimes}
          />
        </Box>

        <Divider sx={{ mx: { xs: 2, md: 4 }, mt: 2, mb: 0, borderColor: 'rgba(0,0,0,0.06)' }} />

        {/* Page content */}
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flex: 1 }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
